import { FC, ReactNode, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { 
  Home, 
  CreditCard, 
  Shield, 
  BarChart3, 
  Menu,
  X,
  ChevronDown,
  Building,
  BookOpen,
  Coins,
  Key
} from 'lucide-react';
import { useAuth } from '../../providers/AuthProvider';
import LogoString  from '../../assets/images/logo_string.svg'

interface UserLayoutProps {
  children?: ReactNode;
}

const userNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'Settlements', href: '/settlements', icon: CreditCard },
  // { name: 'Tokens', href: '/tokens', icon: Coins },
  { name: 'Governance', href: '/governance', icon: Shield },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  // { name: 'Marketplace', href: '/marketplace', icon: Store },
  { name: 'LOOP API', href: '/api-documentation', icon: BookOpen },
  { name: 'API Keys', href: '/api-keys', icon: Key },
  { name: 'Token Factory', href: '/token-factory', icon: Coins },
  { name: 'Company Info', href: '/company-info', icon: Building },
];

export const UserLayout: FC<UserLayoutProps> = () => {
  const { } = useWallet();
  const { user } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0E131A]">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-[#182233]">
          <div className="flex h-16 items-center justify-between px-4">
            {/* <h1 className="text-xl font-bold text-white">Loop Platform</h1> */}
            <img src={LogoString} alt="Loop Platform" className="w-40 h-auto" />
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {userNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-[#2777EF] text-white'
                      : 'text-gray-400 hover:bg-[#567095]/20 hover:text-white'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-[#182233] border-r border-gray-600">
          <div className="flex h-16 items-center px-4">
            {/* <h1 className="text-xl font-bold text-white">Loop Platform</h1> */}
            <img src={LogoString} alt="Loop Platform" className="w-40 h-auto" />
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {userNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-[#2777EF] text-white'
                      : 'text-gray-400 hover:bg-[#567095]/20 hover:text-white'
                  }`}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-2 sm:gap-x-4 border-b border-gray-600 bg-[#182233] px-2 sm:px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-400 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-2 sm:gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            <div className="flex items-center gap-x-2 sm:gap-x-4 lg:gap-x-6">
              {/* Wallet Connection */}
              <div className="hidden sm:block">
                <WalletMultiButton />
              </div>
              <div className="sm:hidden">
                <WalletMultiButton className="!text-xs !px-2 !py-1" />
              </div>
              
              {/* User Menu */}
              <div className="relative">
                <button
                  type="button"
                  className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-medium text-white">
                        {user?.email?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-white">{user?.email}</p>
                      <p className="text-xs text-gray-400">User</p>
                    </div>
                    <ChevronDown className="hidden sm:block h-4 w-4 text-gray-400" />
                  </div>
                </button>

                {userMenuOpen && (
                  <>
                    {/* Backdrop to close menu when clicking outside */}
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div className="origin-top-right absolute right-0 mt-2 w-56 sm:w-64 rounded-xl shadow-xl bg-[#182233] border border-gray-600 focus:outline-none z-50 overflow-hidden">
                      {/* User Info Header */}
                      <div className="px-4 py-3 bg-[#0E131A]/50 border-b border-gray-600">
                        <p className="text-sm font-semibold text-white truncate">{user?.email}</p>
                        <p className="text-xs text-[#2777EF] mt-0.5">Regular User</p>
                      </div>
                      
                      {/* Menu Items */}
                      <div className="py-1">
                        <Link
                          to="/dashboard"
                          className="flex items-center px-4 py-2.5 text-sm text-gray-300 hover:bg-[#2777EF]/10 hover:text-white transition-colors"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                          </svg>
                          Dashboard
                        </Link>
                        <Link
                          to="/settlements"
                          className="flex items-center px-4 py-2.5 text-sm text-gray-300 hover:bg-[#2777EF]/10 hover:text-white transition-colors"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          Settlements
                        </Link>
                        <Link
                          to="/company-info"
                          className="flex items-center px-4 py-2.5 text-sm text-gray-300 hover:bg-[#2777EF]/10 hover:text-white transition-colors"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          Company Info
                        </Link>
                      </div>
                      
                      {/* Sign Out */}
                      <div className="border-t border-gray-600">
                        <button
                          onClick={() => {
                            localStorage.removeItem('token');
                            window.location.href = '/login';
                          }}
                          className="flex items-center w-full px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Sign out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-4 sm:py-6">
          <div className="mx-auto max-w-8xl px-2 sm:px-4 lg:px-6 xl:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default UserLayout;


